<template>
    <div>
        <div class="activity-pane-field no-bottom-padding">
            <ui-textbox
                v-model="activity.title" 
                class="ui-textbox-larger" 
                :placeholder="typePlaceholder"
            />
        </div>

        <div class="activity-pane-field no-top-padding">
            <icon-label-select 
                :options="typeOptions" 
                v-model="activity.medium"
            />
        </div>

        <div class="activity-pane-field">
            <div style="width: 50%; display: inline-block; margin-right: 1rem;" >
                <ui-datepicker 
                    icon="access_time"
                    v-model="activity.due_at"
                    append-dropdown-to-body
                    :start-of-week="1"
                ></ui-datepicker>
            </div>

            <div style="width: 30%; display: inline-block; margin-right: 1rem;" >
                <vue-timepicker
                    v-model="timeProxy"
                    auto-hide-on="minute"
                    :minute-interval="5"
                    ref="timePicker"
                    placeholder="HH:MM"
                    close-on-complete
                    auto-scroll
                >&nbsp;</vue-timepicker>
            </div>
        </div>

        <div v-if="showVehiclePicker" class="activity-pane-field">
            <keen-vehicle-picker
                v-model="vehicleProxy"
                icon="directions_car"
                :allow-addition="false"
            ></keen-vehicle-picker>
        </div>

        <div class="activity-pane-field">
            <ui-textbox
                :multiLine="true"
                :rows="2"
                placeholder="Notes"
                v-model="activity.notes"
                icon="note"
            ></ui-textbox>
        </div>

        <div v-if="!showHiddenFields" style="font-size: 1rem;">
            <div class="material-icons" style="display: inline-block; vertical-align: middle; width: 30px;">link</div>
            <div style="display: inline-block;">
                Set a custom 
                <a href="#" @click.prevent="showHiddenFields = true">
                    lead, contact or owner
                </a>
            </div>
        </div>

        <div v-if="showHiddenFields" class="activity-pane-field">
            <keen-contact-picker
                    placeholder="Contact"
                    v-model="contactProxy"
                    icon="contacts"
                    :relations-to-load="['leads', 'leads.detail']"
            />
        </div>

        <div v-if="showHiddenFields && hasContactWithActivities" class="activity-pane-field">
            <ui-select
                icon="my_location"
                :options="activeContactLeads"
                v-model="leadProxy"
                placeholder="Lead"
            />
        </div>

        <div v-if="showHiddenFields" class="activity-pane-field">
            <keen-team-member-picker icon="face" v-model="ownerProxy" />
        </div>
    </div>
</template>

<style lang="scss">
    .activity-pane-field {
        padding: .25rem 0;
        .ui-textbox-larger input {
            font-size: 1.3rem;
            height: 3rem;
        }
    }
</style>

<script>
    import Moment from 'moment'
    import VueTimepicker from 'vue2-timepicker'
    import 'vue2-timepicker/dist/VueTimepicker.css'
    import { 
        ActivityResource, 
        ActivitySchema, 
        ActivityMediums,
        ActivityMediumsKeyValueList, 
        ActivityMediumsListItemRetriever  
    } from '../../classes/Api/Activity'
    import IconLabelSelect from './controls/IconLabelSelect'
    import {ContactResource} from "../../classes/Api/Contact";
    import KeenVehiclePicker from "./controls/KeenVehiclePicker.vue";
    import {VehicleLifeCycleStatuses} from "../../classes/Api/Vehicle";
    import cloneDeep from "lodash/cloneDeep";

    export default {
        components: {
            KeenVehiclePicker,
            IconLabelSelect,
            VueTimepicker
        },
        props: {
            owners: {
                type: Array,
            },
            showHiddenFieldsByDefault: {
                type: Boolean,
                default: false
            }
        },
        created() {
            this.reset()
        },
        data() {
            return {
                isSaving: true,
                activity: {},
                showHiddenFields: false,
            }
        },
        methods: {
            setActivity(activity) {
                activity = new ActivitySchema(_.cloneDeep(activity))

                if (activity.due_at) {
                    activity.due_at = Moment(activity.due_at).toDate()
                }

                if (activity.contact && !activity.contact.leads) {
                    let params = { with: ['leads', 'leads.detail'] }

                    ContactResource.find(activity.contact.id, params).then(r => {
                        this.$set(activity.contact, 'leads', r.data.leads)

                        this.activity.lead = this.activity.lead ? this.activeContactLeads.find(
                            i => parseInt(i.id) === parseInt(this.activity.lead_id)
                        ) : {}
                    }).catch(handleApiError)
                }

                this.$set(this, 'activity', activity)
            },
            updateActivity(data) {
                let updates = Object.assign(this.activity, data)
                this.$set(this, 'activity', updates)
            },
            loadActivity(id) {
                return new Promise((resolve, reject) => {
                    let relations = ['contact', 'lead', 'lead.detail', 'owner', 'instance'];
                    
                    ActivityResource.find(id, { with: relations}).then(response => {
                        this.setActivity(response.data)
                        resolve(response.data)
                    }).catch(e => handleApiError(e, reject))
                })
            },
            reset() {
                this.showHiddenFields = this.showHiddenFieldsByDefault
                
                this.$set(this, 'activity', new ActivitySchema({
                    medium: ActivityMediums.CALL,
                    due_at: Moment().add(1, 'day').hours(9).minutes(0).seconds(0).toDate(),
                    owner_id: window.marque.ui.user.id,
                    owner: window.marque.ui.user,
                    lead: {},
                    contact: {},
                }))
            },
            save() {
                this.isSaving = true

                // Set the defaults.
                for (let i in this.owners) {
                    switch (this.owners[i].type) {
                        case 'contact':
                            this.activity.contact = this.owners[i].object
                            this.activity.contact_id = this.owners[i].id
                            break;
                        case 'lead':
                            this.activity.lead = this.owners[i].object
                            this.activity.lead_id = this.owners[i].id
                            break;
                    }
                }

                let data = cloneDeep(this.activity)

                this.$emit('saving', data)

                if (!data.title) {
                    data.title = ActivityMediumsListItemRetriever(data.medium).label
                }

                return ActivityResource.storeOrUpdate(this.activity.id, data).then((response) => {
                    this.isSaving = false
                    this.$emit('saved', response.data)
                }).catch(e => {
                    handleApiError(e)
                    this.$emit('error', e)
                })
            },
            addActivityListLabel(l) {
                if (l.detail) {
                    let detail = l.detail.map(d => {
                        return d.short_label
                    })

                    l.label = detail.join(' + ')

                    return l
                } else {
                    l.label = 'Unnamed Lead'
                }

                return l
            }
        },
        computed: {
            showVehiclePicker() {
                return true
            },
            VehicleLifeCycleStatuses() {
                return VehicleLifeCycleStatuses
            },
            hasContactWithActivities() {
                return !!this.activity.contact_id && this.activeContactLeads.length > 0
            },
            typePlaceholder() {
                if (this.activity.medium) {
                    return ActivityMediumsListItemRetriever(this.activity.medium).label
                }
            },
            typeOptions() {
                return ActivityMediumsKeyValueList
            },
            activeContactLeads() {
                if (this.activity && this.activity.contact && this.activity.contact.leads) {
                    return this.activity.contact.leads.filter(item => {
                        return !item.outcome_at
                    }).map(item => {
                        return this.addActivityListLabel(item)
                    })
                }

                return []
            },
            contactProxy: {
                get() {
                    return this.activity.contact
                },
                set(value) {
                    this.activity.contact = value
                    this.activity.contact_id = value.id

                    if (this.activeContactLeads.length === 1) {
                        this.activity.lead = this.activeContactLeads[0]
                        this.activity.lead_id = this.activeContactLeads[0].id
                    }
                }
            },
            vehicleProxy: {
                get() {
                    return this.activity.instance
                },
                set(value) {
                    this.activity.instance = value
                    this.activity.instance_id = value.id
                }
            },
            leadProxy: {
                get() {
                    return this.activity.lead ? this.activity.lead : {}
                },
                set(lead) {
                    this.activity.lead = lead
                    this.activity.lead_id = lead.id
                }
            },
            ownerProxy: {
                get() {
                    return this.activity.owner
                },
                set(user) {
                    this.activity.owner = user
                    this.activity.owner_id = user.id
                }
            },
            timeProxy: {
                get() {
                    if (!this.activity.due_at || this.activity.due_at.getHours() === 0 && this.activity.due_at.getMinutes() === 0) {
                        this.$nextTick(() => {
                            if (this.$refs.timePicker) {
                                this.$refs.timePicker.clearTime()
                            }
                        })
                        return '09:00'
                    }

                    return Moment(this.activity.due_at).format('HH:mm:ss')
                },
                set(value) {
                    if (typeof value === 'string' && this.activity.due_at) {
                        let parts = value.split(':')
                        if (parts.length >= 2) {
                            if (!isNaN(Number(parts[0]))) {
                                this.activity.due_at.setHours(parts[0])
                            }

                            if (!isNaN(Number(parts[1]))) {
                                this.activity.due_at.setMinutes(parts[1])
                            }
                        } else {
                            this.activity.due_at.setHours(0)
                            this.activity.due_at.setMinutes(0)
                        }
                    } else  if (typeof value === 'object' && this.activity.due_at) {
                        this.activity.due_at.setHours(value.HH)
                        this.activity.due_at.setMinutes(value.mm)
                    }
                }
            }
        }
    }
</script>